import {
  Box,
  Container,
  Grid,
  Hidden,
  LinearProgress,
  Link,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { handlePagingAll } from "../../components/api/APIUtilities";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import ListResponsive from "../../components/other/ListResponsive";
import { postNotification } from "../../components/utilities/Notifications";
import {
  findGetParameter,
  getData,
} from "../../components/utilities/UtilityFunctions";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { getSearchProps } from "./utils";

export default function EventPlacingsTruncated(props) {
  const embed =
    props.appRoute || props.embed || findGetParameter("embed") === "true";
  const leagueId = props.leagueId || findGetParameter("league");
  let { eventId } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [event, setEvent] = useState(null);
  const [league, setLeague] = useState(null);
  const [placings, setPlacings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const go = async () => {
      try {
        if (!eventId || !leagueId) {
          throw new Error("Missing eventId or leagueId");
        }

        var apiLeague = await getData(`/leagues/${leagueId}`, {
          queryStringParameters: {
            "expand[]": ["gameSystem", "gameStore", "gameStoreUser"],
          },
        });

        if (
          apiLeague.hidePlacings &&
          authContext?.authState?.identity?.isAdmin !== true &&
          !authContext?.authState?.identity?.adminGameSystems?.includes(
            apiLeague?.gameSystemId
          )
        ) {
          if (
            !apiLeague.gameStoreUser?.active ||
            (!apiLeague.gameStoreUser?.owner &&
              !apiLeague.gameStoreUser?.permissions?.createLeagues)
          ) {
            let error = new Error(
              "The Circuit owner currently has Rankings hidden."
            );
            error.name = "HidePlacings Error";
            throw error;
          }
        }
        setLeague(apiLeague);

        var apiEvent = await getData(`/events/${eventId}`, {
          queryStringParameters: {
            "expand[]": ["gameSystem", "gameStore", "gameStoreUser"],
          },
        });
        setEvent(apiEvent);

        setLoading(true);

        const handleSetPlacings = (data) => {
          data.sort((a, b) => {
            if (a.placing !== undefined && a.placing === undefined) return -1;
            if (a.placing === undefined && a.placing !== undefined) return 1;

            return a.placing - b.placing;
          });
          setPlacings(data);
        };

        var apiPlacings = await handlePagingAll(
          "/eventplacings",
          {
            sortAscending: false,
            eventId: eventId,
            leagueId: leagueId,
            "expand[]": ["user", "team", "army", "subFaction", "character"],
          },
          handleSetPlacings
        );

        apiPlacings.sort((a, b) => {
          if (a.placing !== undefined && a.placing === undefined) return -1;
          if (a.placing === undefined && a.placing !== undefined) return 1;

          return a.placing - b.placing;
        });
        setPlacings(apiPlacings);

        setLoading(false);
      } catch (err) {
        postNotification("error", err);
        navigate("/");
      }
    };

    go();
    // eslint-disable-next-line
  }, []);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      {!placings && <ModalLoadingFull />}
      <Box sx={{ p: 1 }}>
        <Link
          href={`/event/${eventId}`}
          target="_blank"
          underline="none"
          color="inherit"
        >
          <Typography variant="h3" gutterBottom>
            {event?.name}
          </Typography>
        </Link>
      </Box>

      {Boolean(placings) && (
        <Box sx={{ p: 1 }}>
          <ListResponsive
            originalList={placings}
            doSearch
            searchProps={getSearchProps(Boolean(event?.teamEvent))}
            rowProps={{
              headings: (
                <Grid
                  item
                  xs={12}
                  sx={{
                    p: "2px",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: "4px 4px 0 0",
                    backgroundColor: "table.headerBackground",
                    color: "table.headerColor",
                  }}
                >
                  <PlayerRow
                    heading
                    event={event}
                    league={league}
                    numberOfPlacings={placings?.length}
                    embed={embed}
                  />
                </Grid>
              ),
              mapFunction: (row, index) => (
                <Grid
                  item
                  xs={12}
                  key={index}
                  sx={{
                    p: "2px",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  }}
                >
                  <PlayerRow
                    player={row}
                    index={index}
                    event={event}
                    league={league}
                    numberOfPlacings={placings?.length}
                    embed={embed}
                  />
                </Grid>
              ),
            }}
          />
          {loading && (
            <Box sx={{ mt: 1, height: "50px" }}>
              <LinearProgress />
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
}

function PlayerRow(props) {
  const { heading, player, event, league, embed } = props;

  var rankingPoints = heading ? "" : player?.points;

  return (
    <Grid
      container
      alignItems="center"
      spacing={1}
      sx={{
        fontSize: "14px",
        overflow: "hidden",
        textAlign: "center",
      }}
    >
      <Grid item xs sx={{ maxWidth: ["50px", "70px"] }}>
        {heading ? "Placing" : `${player.placing}`}
      </Grid>
      <Hidden smUp>
        {heading ? (
          <Grid item xs>
            <Typography>Player</Typography>
          </Grid>
        ) : (
          <Grid item xs>
            <Link
              href={`/user/${player?.userId || player?.user?.id}?league=${league?.id}${
                embed ? "&embed=true" : ""
              }`}
              underline="none"
            >
              <Typography>
                {`${player?.user?.firstName || ""} ${
                  player?.user?.lastName || ""
                }`.trim()}
              </Typography>
            </Link>
            {(player?.army?.name || player?.faction?.name) && (
              <Link
                href={`/rankings/players?league=${league?.id}&army=${
                  player?.armyId || player?.faction?.id
                }${embed ? "&embed=true" : ""}`}
                underline="none"
              >
                <Typography>
                  {player?.army?.name || player?.faction?.name}
                </Typography>
              </Link>
            )}
            {player?.subFaction?.name && (
              <Typography>{player.subFaction.name}</Typography>
            )}
            {player?.team?.name && (
              <Link
                href={`/team/${player?.teamId || player?.team?.id}?league=${league?.id}${
                  embed ? "&embed=true" : ""
                }`}
                underline="none"
              >
                <Typography>{player?.team?.name}</Typography>
              </Link>
            )}
          </Grid>
        )}
      </Hidden>
      <Hidden smDown>
        <Grid item xs sx={{ maxWidth: ["125px", "unset"], overflow: "hidden" }}>
          {heading ? (
            "Player"
          ) : (
            <Link
              href={`/user/${player?.userId || player?.user?.id}?league=${league?.id}${
                embed ? "&embed=true" : ""
              }`}
              underline="none"
            >
              {`${player?.user?.firstName || ""} ${
                player?.user?.lastName || ""
              }`.trim()}
            </Link>
          )}
        </Grid>
        {event?.gameSystem?.hasFactions && (
          <Grid
            item
            xs
            sx={{ maxWidth: ["125px", "unset"], overflow: "hidden" }}
          >
            {heading ? (
              event?.gameSystem?.factionLabel || "Faction"
            ) : player?.army?.name || player?.faction?.name ? (
              <Link
                href={`/rankings/players?league=${league?.id}&army=${
                  player?.armyId || player?.faction?.id
                }${embed ? "&embed=true" : ""}`}
                underline="none"
              >
                {player?.army?.name || player?.faction?.name}
              </Link>
            ) : (
              ""
            )}
          </Grid>
        )}

        {event?.gameSystem?.hasSubFactions && (
          <Grid
            item
            xs
            sx={{ maxWidth: ["125px", "unset"], overflow: "hidden" }}
          >
            {heading
              ? event?.gameSystem?.subFactionLabel || "Sub Faction"
              : player?.subFaction?.name || ""}
          </Grid>
        )}
        <Grid item xs sx={{ maxWidth: ["125px", "unset"], overflow: "hidden" }}>
          {heading ? (
            "Team"
          ) : player?.team?.name ? (
            <Link
              href={`/team/${player?.teamId || player?.team?.id}?league=${league?.id}${
                embed ? "&embed=true" : ""
              }`}
              underline="none"
            >
              {player?.team?.name}
            </Link>
          ) : (
            ""
          )}
        </Grid>
      </Hidden>
      <Grid item xs sx={{ maxWidth: ["60px", "70px"] }}>
        {heading ? "Points" : rankingPoints}
      </Grid>
    </Grid>
  );
}
